import { lazy } from 'react';

import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { SelfServiceCreditCardApplicationModelCreditCardType as CreditCardType } from '@app/types/bank-afterloan-rest-api';
import ErrorPageView from '@features/ErrorPageView';

import { RoutePath } from './routePath';

const ChangeBankBecomeCustomerPage = lazy(
  () => import('../features/ChangeBankCustomerPage')
);
const ChangeBankPage = lazy(() => import('../features/ChangeBankPage'));
const ChangeBankReceiptPage = lazy(
  () => import('../features/ChangeBankReceiptPage')
);

const CreditCardBecomeCustomerPage = lazy(
  () => import('../features/CreditCardBecomeCustomerPage')
);
const CreditCardFormPage = lazy(() => import('../features/CreditCardFormPage'));
const CreditCardProductPage = lazy(
  () => import('../features/CreditCardProductPage')
);
const CreditCardReceiptPage = lazy(
  () => import('../features/CreditCardReceiptPage')
);
const ManageCreditCardPage = lazy(() => import('../features/ManageCreditCard'));
const ChangeCreditLimitReceiptPage = lazy(
  () =>
    import(
      '../features/ManageCreditCardReceiptPage/components/ChangeCreditLimitReceiptPage'
    )
);

const TerminateCreditCardReceiptPage = lazy(
  () =>
    import(
      '../features/ManageCreditCardReceiptPage/components/TerminateCreditCardReceiptPage'
    )
);

const NoCreditCardPage = lazy(
  () => import('../features/ManageCreditCard/components/NoCreditCardPage')
);

const NewCustomerRedirector = lazy(
  () => import('../features/NewCustomerRedirector')
);
const NoLoanCustomerRedirector = lazy(
  () => import('../features/NoLoanCustomerRedirector')
);

const EditMortgagePage = lazy(() => import('../features/EditMortgage'));
const EditMortgageReceiptPage = lazy(
  () => import('../features/EditMortgageReceiptPage')
);
const GoodnessCertificationBecomeCustomerPage = lazy(
  () => import('../features/GoodnessCertificationCustomerPage')
);
const GoodnessCertificationPage = lazy(
  () => import('../features/GoodnessCertification')
);
const GoodnessCertificationReceiptPage = lazy(
  () => import('../features/GoodnessCertificationReceiptPage')
);

const OnlineBankPage = lazy(() => import('../features/OnlineBankPage'));
const OnlineBankReceiptPage = lazy(
  () => import('../features/OnlineBankReceiptPage')
);

const OnlineBankForChildren = lazy(
  () => import('../features/OnlineBankForChildren')
);

const BankForChildrenPUMReceiptPage = lazy(
  () =>
    import(
      '../features/OnlineBankForChildrenReceiptPage/PUMForChildrenReceiptPage'
    )
);

const BankForChildrenBankdIdReceiptPage = lazy(
  () =>
    import(
      '../features/OnlineBankForChildrenReceiptPage/BankIDForChildrenReceiptPage'
    )
);

const BankForChildrenACcountReceiptPage = lazy(
  () =>
    import(
      '../features/OnlineBankForChildrenReceiptPage/AccountForChildrenReceiptPage'
    )
);

const CommonBecomeCustomerPage = lazy(
  () => import('../features/BecomeCustomerPage')
);

const BecomeLoanCustomerPage = lazy(
  () => import('../features/BecomeLoanCustomerPage')
);

function Router() {
  return (
    <BrowserRouter basename="bank/selvbetjening">
      <Routes>
        <Route element={<NewCustomerRedirector />}>
          <Route element={<NoLoanCustomerRedirector />}>
            <Route
              path={RoutePath.EDIT_MORTGAGE}
              element={<EditMortgagePage />}
            />
            <Route
              path={RoutePath.EDIT_MORTGAGE_RECEIPT_PAGE}
              element={<EditMortgageReceiptPage />}
            />
          </Route>
          <Route path={RoutePath.ONLINE_BANK} element={<OnlineBankPage />} />
          <Route
            path={RoutePath.ONLINE_BANK_CHILD}
            element={<OnlineBankForChildren />}
          />
          <Route
            path={RoutePath.ONLINE_BANK_CHILD_PUM_RECEIPT_PAGE}
            element={<BankForChildrenPUMReceiptPage />}
          />
          <Route
            path={RoutePath.ONLINE_BANK_CHILD_BANKID_RECEIPT_PAGE}
            element={<BankForChildrenBankdIdReceiptPage />}
          />
          <Route
            path={RoutePath.ONLINE_BANK_CHILD_ACCOUNT_RECEIPT_PAGE}
            element={<BankForChildrenACcountReceiptPage />}
          />
          <Route
            path={RoutePath.ONLINE_BANK_RECEIPT_PAGE}
            element={<OnlineBankReceiptPage />}
          />

          <Route
            path={RoutePath.CREDIT_CARD_PRODUCT_PAGE}
            element={
              <CreditCardProductPage creditCardType={CreditCardType.STANDARD} />
            }
          />
          <Route
            path={RoutePath.CREDIT_CARD_PLATINUM_PRODUCT_PAGE}
            element={
              <CreditCardProductPage creditCardType={CreditCardType.PLATINUM} />
            }
          />
          <Route
            path={RoutePath.CREDIT_CARD_FORM}
            element={
              <CreditCardFormPage creditCardType={CreditCardType.STANDARD} />
            }
          />
          <Route
            path={RoutePath.CREDIT_CARD_PLATINUM_FORM}
            element={
              <CreditCardFormPage creditCardType={CreditCardType.PLATINUM} />
            }
          />
          <Route
            path={RoutePath.CREDIT_CARD_RECEIPT_PAGE}
            element={
              <CreditCardReceiptPage creditCardType={CreditCardType.STANDARD} />
            }
          />
          <Route
            path={RoutePath.CREDIT_CARD_PLATINUM_RECEIPT_PAGE}
            element={
              <CreditCardReceiptPage creditCardType={CreditCardType.PLATINUM} />
            }
          />
          <Route
            path={RoutePath.EDIT_CREDIT_CARD}
            element={<ManageCreditCardPage />}
          />
          <Route
            path={RoutePath.EDIT_CREDIT_LIMIT_RECEIPT_PAGE}
            element={
              <ChangeCreditLimitReceiptPage
                creditCardType={CreditCardType.STANDARD}
              />
            }
          />
          <Route
            path={RoutePath.EDIT_CREDIT_LIMIT_PLATINUM_RECEIPT_PAGE}
            element={
              <ChangeCreditLimitReceiptPage
                creditCardType={CreditCardType.PLATINUM}
              />
            }
          />
          <Route
            path={RoutePath.EDIT_CARD_TERMINATE_RECEIPT}
            element={<TerminateCreditCardReceiptPage />}
          />
          <Route
            path={RoutePath.EDIT_CARD_NO_CARD}
            element={<NoCreditCardPage />}
          />

          <Route path={RoutePath.CHANGE_BANK} element={<ChangeBankPage />} />
          <Route
            path={RoutePath.CHANGE_BANK_RECEIPT_PAGE}
            element={<ChangeBankReceiptPage />}
          />
          <Route
            path={RoutePath.GOODNESS_CERTIFICATION_OR_CUSTOMER_DECLARATION}
            element={<GoodnessCertificationPage />}
          />
          <Route
            path={
              RoutePath.GOODNESS_CERTIFICATION_OR_CUSTOMER_DECLARATION_RECEIPT_PAGE
            }
            element={<GoodnessCertificationReceiptPage />}
          />
        </Route>
        <Route
          path={RoutePath.CREDIT_CARD_BECOME_CUSTOMER}
          element={
            <CreditCardBecomeCustomerPage
              creditCardType={CreditCardType.STANDARD}
            />
          }
        />
        <Route
          path={RoutePath.EDIT_MORTGAGE_BECOME_CUSTOMER}
          element={<BecomeLoanCustomerPage />}
        />
        <Route
          path={RoutePath.BECOME_CUSTOMER_COMMON_PAGE}
          element={<CommonBecomeCustomerPage />}
        />
        <Route
          path={RoutePath.CREDIT_CARD_PLATINUM_BECOME_CUSTOMER}
          element={
            <CreditCardBecomeCustomerPage
              creditCardType={CreditCardType.PLATINUM}
            />
          }
        />
        <Route
          path={
            RoutePath.GOODNESS_CERTIFICATION_OR_CUSTOMER_DECLARATION_BECOME_CUSTOMER
          }
          element={<GoodnessCertificationBecomeCustomerPage />}
        />
        <Route
          path={RoutePath.CHANGE_BANK_BECOME_CUSTOMER}
          element={<ChangeBankBecomeCustomerPage />}
        />
        <Route path="*" element={<ErrorPageView />} />
      </Routes>
    </BrowserRouter>
  );
}

export default Router;
